import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress, MenuItem, Chip, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, People as PeopleIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getDisciplineOffers, saveDisciplineOffer, deleteDisciplineOffer, getDofStudentCounts, getDofStudentSummary } from '../../../store/actions/disciplineOfferActions';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip
} from '@mui/material';
import { situacaoMatricula } from '../../../utils/utility';
import { fetchPeriods } from '../../../store/actions/reportActions';
import { getClassOffers } from '../../../store/actions/classOfferActions';
import { getTeachers } from '../../../store/actions/teacherActions';
import { getEstruturaLevelDisciplinas } from '../../../store/actions/estruturaDiscActions';
import { RootState } from '../../../store/reducers';
import { createTablePaginationWithFirstLast } from '../../../components/TablePaginationWithFirstLast';

function getCurrentSemesterLabel(): string {
    const now = new Date();
    const year = now.getFullYear();
    const half = now.getMonth() < 6 ? 1 : 2;
    return `${year}-${half}`;
}

const formatClassOfferOption = (classOffer: any) => {
    const description = classOffer?.Description || '';
    const levelDescription = classOffer?.level?.Description;

    return levelDescription ? `${description} (${levelDescription})` : description;
};

const DisciplineOffers = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const disciplineOffers = useSelector((state: RootState) => state.disciplineOffers.disciplineOffers);
    const isFetching = useSelector((state: RootState) => state.disciplineOffers.isFetching);
    const classOffers = useSelector((state: RootState) => state.classOffers.classOffers);
    const teachers = useSelector((state: RootState) => state.teachers.teachers);
    const levels = useSelector((state: RootState) => state.cadastros.estruturaDisciplinas);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [formData, setFormData] = useState<any>({
        ClassOffer_id: '', DisciplineModule_id: '', Discipline_id: '', Teacher_id: '', Period_id: '',
        InitialDate: '', EndDate: '', isEnrollmentClosed: false
    });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [counts, setCounts] = useState<Record<number, { total: number; ativos: number }>>({});
    const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsList, setStudentsList] = useState<any[]>([]);
    const [studentsDof, setStudentsDof] = useState<any | null>(null);
    const [periods, setPeriods] = useState<any[]>([]);
    const [periodId, setPeriodId] = useState<number | ''>('');

    // Carrega periodos e seleciona o atual (yyyy-1 ou yyyy-2)
    useEffect(() => {
        // @ts-ignore
        dispatch(fetchPeriods()).then((list: any[]) => {
            const arr = list || [];
            setPeriods(arr);
            const target = getCurrentSemesterLabel();
            const found = arr.find((p: any) => String(p.Description).trim() === target);
            if (found) setPeriodId(found.id);
        });
        dispatch(getClassOffers());
        dispatch(getTeachers());
        dispatch(getEstruturaLevelDisciplinas());
    }, [dispatch]);

    // Recarrega ofertas e contagens quando o periodo muda
    useEffect(() => {
        if (periodId === '') return;
        dispatch(getDisciplineOffers(null, Number(periodId)));
        getDofStudentCounts(Number(periodId)).then(setCounts);
    }, [dispatch, periodId]);

    const handleOpenStudents = async (dof: any) => {
        setStudentsDof(dof);
        setStudentsList([]);
        setStudentsLoading(true);
        setStudentsDialogOpen(true);
        const data = await getDofStudentSummary(dof.id);
        setStudentsList(data);
        setStudentsLoading(false);
    };

    const enrollmentStatusLabel = (s: any) => {
        const found = situacaoMatricula.find((it: any) => it.id === s);
        return found ? found.label : (s == null ? 'ATIVA' : String(s));
    };

    const enrollmentStatusColor = (s: any): 'success' | 'error' | 'warning' | 'default' => {
        if (s === 2) return 'error';
        if (s === 3) return 'warning';
        if (s === 4) return 'default';
        if (s === 5) return 'success';
        return 'success';
    };

    // Build a flat list of discipline modules from the levels structure
    const disciplineModules: any[] = [];
    if (levels && Array.isArray(levels)) {
        levels.forEach((level: any) => {
            if (level.discipline && Array.isArray(level.discipline)) {
                level.discipline.forEach((disc: any) => {
                    if (disc.disciplineModule && Array.isArray(disc.disciplineModule)) {
                        disc.disciplineModule.forEach((dm: any) => {
                            disciplineModules.push({
                                id: dm.id,
                                Discipline_id: disc.id,
                                label: [disc.Description, dm.Description, level.Description]
                                    .filter(Boolean)
                                    .join(' - '),
                                disciplineDescription: disc.Description || '',
                                moduleDescription: dm.Description || '',
                                levelDescription: level.Description || ''
                            });
                        });
                    }
                });
            }
        });
    }

    const handleOpen = (item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData({
                ClassOffer_id: item.ClassOffer_id || '',
                DisciplineModule_id: item.DisciplineModule_id || '',
                Discipline_id: item.Discipline_id || item.disciplineModule?.Discipline_id || '',
                Teacher_id: item.Teacher_id || '',
                Period_id: item.Period_id || '',
                InitialDate: item.InitialDate ? item.InitialDate.substring(0, 10) : '',
                EndDate: item.EndDate ? item.EndDate.substring(0, 10) : '',
                isEnrollmentClosed: item.isEnrollmentClosed || false
            });
        } else {
            setEditItem(null);
            setFormData({
                ClassOffer_id: '', DisciplineModule_id: '', Discipline_id: '', Teacher_id: '', Period_id: '',
                InitialDate: '', EndDate: '', isEnrollmentClosed: false
            });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({
            ClassOffer_id: '', DisciplineModule_id: '', Teacher_id: '', Period_id: '',
            InitialDate: '', EndDate: '', isEnrollmentClosed: false
        });
    };

    const handleSave = () => {
        const payload = editItem ? { ...formData, id: editItem.id } : { ...formData };
        dispatch(saveDisciplineOffer(payload));
        handleClose();
        enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
    };

    const handleDelete = (id: number) => {
        dispatch(deleteDisciplineOffer(id));
        setDeleteConfirm(null);
        enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
    };

    const handleChange = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const isDifferentLevel = (disciplineLevel: any, classOfferLevel: any) => {
        if (!disciplineLevel || !classOfferLevel) {
            return false;
        }

        if (disciplineLevel.id != null && classOfferLevel.id != null) {
            return String(disciplineLevel.id) !== String(classOfferLevel.id);
        }

        return disciplineLevel.Description !== classOfferLevel.Description;
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                filter: false,
                sort: true,
            }
        },
        {
            name: 'level',
            label: 'NÃ­vel',
            options: {
                filter: true,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const offer = disciplineOffers[dataIndex];
                    const disciplineLevel = offer?.disciplineModule?.discipline?.level;
                    const classOfferLevel = offer?.classOffer?.level;
                    const levelDescription = disciplineLevel?.Description || '';
                    const hasDifferentLevel = isDifferentLevel(disciplineLevel, classOfferLevel);

                    return (
                        <Typography
                            component="span"
                            color={hasDifferentLevel ? 'error' : 'inherit'}
                            sx={{ fontWeight: hasDifferentLevel ? 700 : 400 }}
                        >
                            {levelDescription}
                        </Typography>
                    );
                },
            }
        },
        {
            name: 'disciplineModule',
            label: 'Disciplina',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.discipline?.Description || '',
            }
        },
        {
            name: 'disciplineModule',
            label: 'Módulo',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.Description || '',
            }
        },
        {
            name: 'classOffer',
            label: 'Turma',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.Description || '',
            }
        },
        {
            name: 'teacher',
            label: 'Professor',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.FirstName || '',
            }
        },
        {
            name: 'period',
            label: 'Período',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: any) => value?.Description || value?.id || '',
            }
        },
        {
            name: 'InitialDate',
            label: 'Data Início',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '',
            }
        },
        {
            name: 'EndDate',
            label: 'Data Fim',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '',
            }
        },
        {
            name: 'totalAlunos',
            label: 'Total',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = disciplineOffers[dataIndex];
                    const c = counts[item?.id];
                    return c ? c.total : 0;
                }
            }
        },
        {
            name: 'ativosAlunos',
            label: 'Ativos',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = disciplineOffers[dataIndex];
                    const c = counts[item?.id];
                    return c ? c.ativos : 0;
                }
            }
        },
        {
            name: 'isEnrollmentClosed',
            label: 'Matrícula',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: boolean) => (
                    <Chip
                        label={value ? 'Fechada' : 'Aberta'}
                        color={value ? 'error' : 'success'}
                        size="small"
                    />
                ),
            }
        },
        {
            name: 'actions',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = disciplineOffers[dataIndex];
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Alunos">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenStudents(item);
                                    }}
                                >
                                    <PeopleIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteConfirm(item.id);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        customFooter: createTablePaginationWithFirstLast([15, 30, 50]),
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            const item = disciplineOffers[rowMeta.dataIndex];
            handleOpen(item);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                <Typography variant="h5">Ofertas de Disciplinas</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Período"
                        select
                        size="small"
                        value={periodId}
                        onChange={(e) => setPeriodId(e.target.value === '' ? '' : Number(e.target.value))}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">
                            <em>Todos</em>
                        </MenuItem>
                        {periods.map((p: any) => (
                            <MenuItem key={p.id} value={p.id}>{p.Description}</MenuItem>
                        ))}
                    </TextField>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                        Nova Oferta
                    </Button>
                </Box>
            </Box>

            <Paper>
                <MUIDataTable
                    title=""
                    data={disciplineOffers || []}
                    columns={columns}
                    options={options}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Oferta de Disciplina' : 'Nova Oferta de Disciplina'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Turma"
                            select
                            value={formData.ClassOffer_id}
                            onChange={(e) => handleChange('ClassOffer_id', e.target.value)}
                            fullWidth
                            required
                        >
                            {(classOffers || []).map((co: any) => (
                                <MenuItem key={co.id} value={co.id}>
                                    {formatClassOfferOption(co)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Disciplina / Módulo"
                            select
                            value={formData.DisciplineModule_id}
                            onChange={(e) => {
                                const dmId = e.target.value;
                                const dm = disciplineModules.find((d) => d.id === dmId);
                                setFormData((prev: any) => ({
                                    ...prev,
                                    DisciplineModule_id: dmId,
                                    Discipline_id: dm?.Discipline_id || ''
                                }));
                            }}
                            fullWidth
                            required
                        >
                            {disciplineModules.map((dm: any) => (
                                <MenuItem key={dm.id} value={dm.id}>
                                    {dm.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Professor"
                            select
                            value={formData.Teacher_id}
                            onChange={(e) => handleChange('Teacher_id', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">
                                <em>Nenhum</em>
                            </MenuItem>
                            {(teachers || []).map((t: any) => (
                                <MenuItem key={t.id} value={t.id}>
                                    {t.FirstName}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Período (ID)"
                            type="number"
                            value={formData.Period_id}
                            onChange={(e) => handleChange('Period_id', e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Data Início"
                            type="date"
                            value={formData.InitialDate}
                            onChange={(e) => handleChange('InitialDate', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Data Fim"
                            type="date"
                            value={formData.EndDate}
                            onChange={(e) => handleChange('EndDate', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isEnrollmentClosed}
                                    onChange={(e) => handleChange('isEnrollmentClosed', e.target.checked)}
                                />
                            }
                            label="Matrícula Fechada"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!formData.ClassOffer_id || !formData.DisciplineModule_id}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Students Dialog */}
            <Dialog open={studentsDialogOpen} onClose={() => setStudentsDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Alunos - DOF #{studentsDof?.id}
                    {studentsDof?.disciplineModule?.discipline?.Description && ` - ${studentsDof.disciplineModule.discipline.Description}`}
                    {studentsDof?.classOffer?.Description && ` - ${studentsDof.classOffer.Description}`}
                </DialogTitle>
                <DialogContent>
                    {studentsLoading ? (
                        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                    ) : studentsList.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            Nenhum aluno matriculado nesta oferta.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Aluno</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Carnê</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Matrícula</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Situação Disc.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Nota</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Freq. %</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Resultado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {studentsList.map((s: any) => {
                                        const nome = (s.socialName || s.personName || '') + (s.lastName ? ` ${s.lastName}` : '');
                                        return (
                                            <TableRow key={s.deeId} hover>
                                                <TableCell>{nome.trim() || `#${s.personId}`}</TableCell>
                                                <TableCell>{s.paymentCard || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={enrollmentStatusLabel(s.enrollmentStatus)}
                                                        size="small"
                                                        color={enrollmentStatusColor(s.enrollmentStatus)}
                                                    />
                                                </TableCell>
                                                <TableCell>{s.deeStatusDescription || (s.deeStatus != null ? `#${s.deeStatus}` : '-')}</TableCell>
                                                <TableCell align="right">{s.nota != null ? Number(s.nota).toFixed(1).replace('.', ',') : '-'}</TableCell>
                                                <TableCell align="right">{s.frequencia != null ? `${Number(s.frequencia).toFixed(0)}%` : '-'}</TableCell>
                                                <TableCell>{s.resultado || '-'}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStudentsDialogOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir esta oferta de disciplina?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button
                        onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DisciplineOffers;
